import {
  ChevronDown,
  ChevronRight,
  Loader2,
  Package,
  Trash2,
} from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  AdminBulkActionRow,
  AdminSaveBar,
  AdminSectionCard,
  EmptyState,
  HelpAnchor,
  IconButton,
  InlineError,
  InputField,
  Pill,
  SkeletonForm,
  SkeletonRows,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  TextareaField,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import {
  ADMIN_ACTION_BUTTON,
  BUTTON_PRIMARY,
} from '@/shared/ui-class';
import {
  ADMIN_SEED_ANCHOR_MS,
  SEED_SANDBOX_LIBRARIES_LOAD,
  type SandboxDeployedRecord,
  type SandboxDesiredManifest,
  type SandboxPythonPackage,
} from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell, formatAdminDateTime } from './admin-shared';
import {
  applyDocumentToolchainPreset,
  buildDockerfileSnippet,
  cleanManifest,
  EMPTY_SANDBOX_DESIRED_MANIFEST,
  EXACT_VERSION_PATTERN,
  formatDeployedDate,
  isManifestDirty,
  isValidPythonVersion,
  mockPypiVerify,
  normalizePypiName,
  PRESET_LABEL,
  PYTHON_PACKAGE_NAME_PATTERN,
  rowKey,
  statusLineMode,
  type PypiVerifyResult,
  type RowVerifyState,
} from './SandboxLibrariesScene.transform';

function StatusLine({
  deployed,
  drift,
}: {
  deployed: SandboxDeployedRecord | null;
  drift: boolean;
}) {
  const mode = statusLineMode(deployed, drift);

  if (mode === 'live' && deployed) {
    const date = formatDeployedDate(deployed.builtAt, formatAdminDateTime);
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-divider bg-zinc-50 px-4 py-3 dark:bg-zinc-900/40">
        <Pill tone="success">Live</Pill>
        <span className="text-sm text-muted">
          Snapshot{' '}
          <code className="text-zinc-700 dark:text-zinc-300">{deployed.snapshotId}</code>
          {date ? <> · baked {date}</> : null}
        </span>
      </div>
    );
  }

  if (mode === 'drift') {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-divider bg-zinc-50 px-4 py-3 dark:bg-zinc-900/40">
        <Pill tone="warning">Changes not baked</Pill>
        <span className="text-sm text-muted">
          The saved list differs from the live snapshot — re-bake and record the new
          snapshot under Advanced.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-divider bg-zinc-50 px-4 py-3 dark:bg-zinc-900/40">
      <Pill>Not live yet</Pill>
      <span className="text-sm text-muted">
        This list hasn&apos;t been baked into a sandbox image yet. When you&apos;re ready,
        open Advanced, copy the Dockerfile snippet into your image build, then record the
        snapshot ID so this page can track what&apos;s live.
      </span>
    </div>
  );
}

function RowVerifyPill({
  state,
  version,
}: {
  state: RowVerifyState;
  version: string;
}) {
  if (state.status === 'idle') return <Pill>Unverified</Pill>;
  if (state.status === 'checking') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking…
      </span>
    );
  }

  const result = state.result;
  if (result.status === 'not_found') return <Pill tone="danger">Not on PyPI</Pill>;
  if (result.status === 'unreachable') {
    return <Pill tone="warning">Couldn&apos;t verify</Pill>;
  }

  const isRange =
    version.trim() !== '' && !EXACT_VERSION_PATTERN.test(version.trim());
  if (isRange) return <Pill>Range — name checked</Pill>;
  if (result.version === null) return <Pill tone="success">On PyPI</Pill>;
  if (result.version.status === 'missing') {
    return <Pill tone="danger">Version not found</Pill>;
  }
  if (result.version.status === 'yanked') return <Pill tone="warning">Yanked</Pill>;
  return <Pill tone="success">On PyPI</Pill>;
}

function AddPackageForm({
  draft,
  onAdd,
  autoVerifySuspended,
  onRateLimited: _onRateLimited,
}: {
  draft: SandboxDesiredManifest;
  onAdd: (pkg: SandboxPythonPackage, verifyResult: PypiVerifyResult | null) => void;
  autoVerifySuspended: boolean;
  onRateLimited: () => void;
}) {
  const [name, setName] = useState('');
  const [version, setVersion] = useState('');
  const [verifyState, setVerifyState] = useState<
    | { status: 'idle' }
    | { status: 'checking' }
    | { status: 'done'; result: PypiVerifyResult }
    | { status: 'range' }
    | { status: 'rate-limited' }
  >({ status: 'idle' });
  const [duplicateError, setDuplicateError] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runVerify = useCallback(
    async (pkgName: string, ver: string) => {
      if (!PYTHON_PACKAGE_NAME_PATTERN.test(pkgName)) {
        setVerifyState({ status: 'idle' });
        return;
      }
      setVerifyState({ status: 'checking' });
      const result = mockPypiVerify(pkgName, ver, ADMIN_SEED_ANCHOR_MS);
      setVerifyState({ status: 'done', result });
    },
    [],
  );

  const handleNameChange = (val: string) => {
    setName(val);
    setDuplicateError(false);
    setVerifyState({ status: 'idle' });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim() || !PYTHON_PACKAGE_NAME_PATTERN.test(val.trim())) return;
    if (autoVerifySuspended) return;
    debounceRef.current = setTimeout(() => {
      void runVerify(val.trim(), version);
    }, 450);
  };

  const handleVersionChange = (val: string) => {
    setVersion(val);
    if (!val.trim()) return;
    if (!EXACT_VERSION_PATTERN.test(val.trim())) {
      if (val.trim() && isValidPythonVersion(val.trim())) {
        setVerifyState({ status: 'range' });
      }
      return;
    }
    if (name.trim() && PYTHON_PACKAGE_NAME_PATTERN.test(name.trim()) && !autoVerifySuspended) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void runVerify(name.trim(), val.trim());
      }, 450);
    }
  };

  const handleAdd = () => {
    const pkgName = name.trim();
    const pkgVersion = version.trim();
    if (!pkgName || !PYTHON_PACKAGE_NAME_PATTERN.test(pkgName)) return;
    const alreadyExists = draft.python.some(
      (pkg) =>
        normalizePypiName(pkg.name) === normalizePypiName(pkgName) &&
        pkg.version === pkgVersion,
    );
    if (alreadyExists) {
      setDuplicateError(true);
      return;
    }
    const result = verifyState.status === 'done' ? verifyState.result : null;
    onAdd({ name: pkgName, version: pkgVersion }, result);
    setName('');
    setVersion('');
    setVerifyState({ status: 'idle' });
    setDuplicateError(false);
    nameRef.current?.focus();
  };

  const handleManualVerify = () => {
    if (!name.trim() || !PYTHON_PACKAGE_NAME_PATTERN.test(name.trim())) return;
    void runVerify(name.trim(), version);
  };

  const canAdd = name.trim() !== '' && PYTHON_PACKAGE_NAME_PATTERN.test(name.trim());
  const canonicalHint =
    verifyState.status === 'done' &&
    verifyState.result.status === 'found' &&
    normalizePypiName(verifyState.result.requestedName) !==
      normalizePypiName(verifyState.result.canonicalName)
      ? verifyState.result.canonicalName
      : null;
  const latestVersion =
    verifyState.status === 'done' && verifyState.result.status === 'found'
      ? verifyState.result.latestVersion
      : null;

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[10rem] flex-1">
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted">
            Package name
          </label>
          <InputField
            ref={nameRef}
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="python-docx"
          />
        </div>
        <div className="min-w-[8rem] flex-[0_0_9rem]">
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted">
            Version
          </label>
          <InputField
            value={version}
            onChange={(e) => handleVersionChange(e.target.value)}
            placeholder="1.2.0"
          />
        </div>
        <button type="button" disabled={!canAdd} onClick={handleAdd} className={BUTTON_PRIMARY}>
          Add
        </button>
      </div>

      <div className="mt-2 min-h-[1.25rem]">
        {verifyState.status === 'checking' && (
          <span className="inline-flex items-center gap-1 text-sm text-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Looking up on PyPI…
          </span>
        )}
        {verifyState.status === 'done' && verifyState.result.status === 'found' && (
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone="success">
              <code className="text-xs">{verifyState.result.canonicalName}</code>
              {' · '}latest {verifyState.result.latestVersion}
            </Pill>
            {verifyState.result.summary ? (
              <span className="text-sm text-muted">{verifyState.result.summary}</span>
            ) : null}
            {latestVersion ? (
              <button
                type="button"
                onClick={() => handleVersionChange(latestVersion)}
                className={ADMIN_ACTION_BUTTON}
              >
                Pin {latestVersion}
              </button>
            ) : null}
            {canonicalHint ? (
              <button
                type="button"
                onClick={() => {
                  setName(canonicalHint);
                  setDuplicateError(false);
                }}
                className={ADMIN_ACTION_BUTTON}
              >
                Use <code className="text-xs">{canonicalHint}</code>
              </button>
            ) : null}
          </div>
        )}
        {verifyState.status === 'done' && verifyState.result.status === 'not_found' && (
          <Pill tone="danger">Not on PyPI — check spelling on pypi.org</Pill>
        )}
        {verifyState.status === 'done' && verifyState.result.status === 'unreachable' && (
          <Pill tone="warning">Couldn&apos;t reach PyPI — you can still add</Pill>
        )}
        {verifyState.status === 'rate-limited' && (
          <Pill tone="warning">Too many lookups — pausing checks for a minute</Pill>
        )}
        {verifyState.status === 'range' && <Pill>Range — resolved at bake time</Pill>}
        {duplicateError ? <Pill tone="danger">Already in the list</Pill> : null}
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted">
          Use the package&apos;s name on pypi.org (e.g.{' '}
          <code className="text-xs">python-docx</code>) and pin an exact version (e.g.{' '}
          <code className="text-xs">1.2.0</code>) so every rebuild is reproducible.
        </p>
        {name.trim() && PYTHON_PACKAGE_NAME_PATTERN.test(name.trim()) ? (
          <button
            type="button"
            onClick={handleManualVerify}
            className="text-xs text-muted underline hover:text-zinc-800 dark:hover:text-zinc-200"
          >
            Verify
          </button>
        ) : null}
      </div>
    </div>
  );
}

function PythonTable({
  packages,
  rowVerify,
  onRemove,
  onVerifyRow,
  onPreset,
}: {
  packages: SandboxPythonPackage[];
  rowVerify: Map<string, RowVerifyState>;
  onRemove: (index: number) => void;
  onVerifyRow: (index: number) => void;
  onPreset: () => void;
}) {
  if (packages.length === 0) {
    return (
      <EmptyState
        embedded
        icon={<Package className="h-8 w-8 text-zinc-400" />}
        title="No Python packages yet"
        description="Packages added here are baked into the code-sandbox image, so every chat can import them. Start with the document toolchain, or add any package from PyPI above."
        action={
          <button type="button" onClick={onPreset} className={ADMIN_ACTION_BUTTON}>
            {PRESET_LABEL}
          </button>
        }
      />
    );
  }

  return (
    <Table wrap={false}>
      <THead>
        <TR>
          <TH>Package</TH>
          <TH>Version</TH>
          <TH>Status</TH>
          <TH className="w-16" />
        </TR>
      </THead>
      <TBody>
        {packages.map((pkg, index) => {
          const key = rowKey(pkg);
          const state: RowVerifyState = rowVerify.get(key) ?? { status: 'idle' };
          return (
            <TR key={index}>
              <TD>
                <code className="text-xs">{pkg.name}</code>
              </TD>
              <TD>
                <code className="text-xs">{pkg.version || '—'}</code>
              </TD>
              <TD>
                <div className="flex flex-wrap items-center gap-2">
                  <RowVerifyPill state={state} version={pkg.version} />
                  <button
                    type="button"
                    onClick={() => onVerifyRow(index)}
                    disabled={state.status === 'checking'}
                    className="text-xs text-muted underline hover:text-zinc-700 disabled:opacity-40 dark:hover:text-zinc-200"
                  >
                    Verify
                  </button>
                </div>
              </TD>
              <TD className="text-right">
                <IconButton label="Remove package" danger onClick={() => onRemove(index)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </IconButton>
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}

function AdvancedSection({
  defaultOpen,
  draft,
  dockerfileSnippet,
  snapshotIdInput,
  digestInput,
  marking,
  markError,
  onUpdateApt,
  onRemoveApt,
  onAddApt,
  onNotesChange,
  onSnapshotIdChange,
  onDigestChange,
  onMarkDeployed,
}: {
  defaultOpen: boolean;
  draft: SandboxDesiredManifest;
  dockerfileSnippet: string;
  snapshotIdInput: string;
  digestInput: string;
  marking: boolean;
  markError: string | null;
  onUpdateApt: (index: number, value: string) => void;
  onRemoveApt: (index: number) => void;
  onAddApt: () => void;
  onNotesChange: (value: string) => void;
  onSnapshotIdChange: (value: string) => void;
  onDigestChange: (value: string) => void;
  onMarkDeployed: () => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(dockerfileSnippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <AdminSectionCard className="overflow-hidden p-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-900/40"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted" />
        )}
        Advanced
      </button>

      {open ? (
        <div className="space-y-6 p-4">
          <p className="text-sm text-muted">
            For the operator doing the image bake: system (apt) packages, notes, the
            Dockerfile snippet to copy into the build, and where to record the finished
            snapshot.
          </p>

          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
              Apt packages
            </p>
            <div className="space-y-2">
              {draft.apt.map((pkg, index) => (
                <div key={index} className="flex items-center gap-2">
                  <InputField
                    value={pkg}
                    onChange={(e) => onUpdateApt(index, e.target.value)}
                    placeholder="libreoffice"
                  />
                  <IconButton label="Remove apt package" danger onClick={() => onRemoveApt(index)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </IconButton>
                </div>
              ))}
              <button
                type="button"
                onClick={onAddApt}
                className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-zinc-800 dark:hover:text-zinc-200"
              >
                + Add apt package
              </button>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted">
              Notes
            </label>
            <TextareaField
              value={draft.notes}
              onChange={(e) => onNotesChange(e.target.value)}
              rows={4}
              placeholder="Operator notes (bake context, registry, etc.)"
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-muted">
                Dockerfile snippet
              </p>
              <button
                type="button"
                onClick={handleCopy}
                className="text-xs text-muted underline hover:text-zinc-700 dark:hover:text-zinc-200"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre className="overflow-x-auto rounded-lg border border-divider bg-zinc-50 p-4 text-xs text-zinc-800 dark:bg-zinc-950 dark:text-zinc-200">
              {dockerfileSnippet}
            </pre>
          </div>

          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
              Record deployed snapshot
            </p>
            <p className="mb-3 text-sm text-muted">
              After baking and pushing, record the snapshot id that matches this manifest.
            </p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted">
                  Snapshot id
                </label>
                <InputField
                  value={snapshotIdInput}
                  onChange={(e) => onSnapshotIdChange(e.target.value)}
                  placeholder="daytona-snapshot-id"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted">
                  Digest (optional)
                </label>
                <InputField
                  value={digestInput}
                  onChange={(e) => onDigestChange(e.target.value)}
                  placeholder="sha256:…"
                />
              </div>
              {markError ? <InlineError>{markError}</InlineError> : null}
              <button
                type="button"
                disabled={marking || !snapshotIdInput.trim()}
                onClick={onMarkDeployed}
                className={cn(ADMIN_ACTION_BUTTON, 'inline-flex items-center gap-2 px-4 py-2')}
              >
                {marking ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Mark deployed
              </button>
            </div>
          </div>

          <p className="text-sm text-muted">
            Runtime snapshot selection still uses{' '}
            <code className="text-xs">DAYTONA_WORKSPACE_TEMPLATE</code> — wiring admin deploy
            to the provider is tracked in <code className="text-xs">docs/roadmap.md</code>{' '}
            (ADM-2.5 follow-up).
          </p>
        </div>
      ) : null}
    </AdminSectionCard>
  );
}

export function SandboxLibrariesScene() {
  const [initial, setInitial] = useState(SEED_SANDBOX_LIBRARIES_LOAD.desired);
  const [draft, setDraft] = useState(SEED_SANDBOX_LIBRARIES_LOAD.desired);
  const [deployed, setDeployed] = useState(SEED_SANDBOX_LIBRARIES_LOAD.deployed);
  const [drift, setDrift] = useState(SEED_SANDBOX_LIBRARIES_LOAD.drift);
  const [loading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState(false);
  const [error] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [markError, setMarkError] = useState<string | null>(null);
  const [snapshotIdInput, setSnapshotIdInput] = useState(
    SEED_SANDBOX_LIBRARIES_LOAD.deployed?.snapshotId ?? '',
  );
  const [digestInput, setDigestInput] = useState(
    SEED_SANDBOX_LIBRARIES_LOAD.deployed?.digest ?? '',
  );
  const [rowVerify, setRowVerify] = useState<Map<string, RowVerifyState>>(new Map());
  const [autoVerifySuspended, setAutoVerifySuspended] = useState(false);

  const handleRateLimited = useCallback(() => {
    setAutoVerifySuspended(true);
    setTimeout(() => setAutoVerifySuspended(false), 60_000);
  }, []);

  const dirty = useMemo(() => isManifestDirty(initial, draft), [draft, initial]);
  const dockerfileSnippet = useMemo(
    () => buildDockerfileSnippet(draft ?? EMPTY_SANDBOX_DESIRED_MANIFEST),
    [draft],
  );

  const applyPreset = () => {
    setDraft((prev) => applyDocumentToolchainPreset(prev));
  };

  const onSave = () => {
    const cleaned = cleanManifest(draft);
    setSaving(true);
    setSaveError(null);
    setInitial(cleaned);
    setDraft(cleaned);
    setSaving(false);
  };

  const onDiscard = () => {
    setDraft(initial);
    setSaveError(null);
    setRowVerify(new Map());
  };

  const onMarkDeployed = () => {
    setMarking(true);
    setMarkError(null);
    setDeployed({
      snapshotId: snapshotIdInput,
      builtAt: ADMIN_SEED_ANCHOR_MS,
      digest: digestInput,
      manifestHash: SEED_SANDBOX_LIBRARIES_LOAD.deployed?.manifestHash ?? 'mock-hash',
    });
    setDrift(false);
    setMarking(false);
  };

  const removePython = (index: number) => {
    setDraft({ ...draft, python: draft.python.filter((_, i) => i !== index) });
  };

  const handleAddPackage = (
    pkg: SandboxPythonPackage,
    verifyResult: PypiVerifyResult | null,
  ) => {
    setDraft({ ...draft, python: [...draft.python, pkg] });
    if (verifyResult !== null) {
      const key = rowKey(pkg);
      setRowVerify((prev) => {
        const next = new Map(prev);
        next.set(key, { status: 'done', result: verifyResult });
        return next;
      });
    }
  };

  const handleVerifyRow = (index: number) => {
    const pkg = draft.python[index];
    if (!pkg) return;
    const key = rowKey(pkg);
    setRowVerify((prev) => {
      const next = new Map(prev);
      next.set(key, { status: 'checking' });
      return next;
    });
    const result = mockPypiVerify(pkg.name, pkg.version, ADMIN_SEED_ANCHOR_MS);
    setRowVerify((prev) => {
      const next = new Map(prev);
      next.set(key, { status: 'done', result });
      return next;
    });
  };

  const packageCount = draft.python.length;

  return (
    <AdminSceneShell
      sceneName="SandboxLibrariesScene"
      title="Sandbox libraries"
      description="Python packages available inside the code sandbox."
      headerActions={<HelpAnchor doc="workspace/admin" />}
      stackClassName="space-y-6"
    >
      <Commentable name="Panel">
        <div className="space-y-6">
          {error ? <InlineError>{error}</InlineError> : null}

          {loading ? (
            <>
              <div className="rounded-lg border border-divider bg-zinc-50 px-4 py-3 dark:bg-zinc-900/40">
                <SkeletonForm fields={1} />
              </div>
              <AdminSectionCard title="Add Python package">
                <SkeletonForm fields={2} />
              </AdminSectionCard>
              <AdminSectionCard title="Python packages">
                <SkeletonRows count={4} cols={4} />
              </AdminSectionCard>
            </>
          ) : (
            <>
              <StatusLine deployed={deployed} drift={drift} />

              <AdminSectionCard title="Add Python package">
                <AddPackageForm
                  draft={draft}
                  onAdd={handleAddPackage}
                  autoVerifySuspended={autoVerifySuspended}
                  onRateLimited={handleRateLimited}
                />
              </AdminSectionCard>

              <AdminSectionCard title="Python packages">
                <AdminBulkActionRow
                  left={
                    <>
                      {packageCount} package{packageCount === 1 ? '' : 's'}
                    </>
                  }
                >
                  {packageCount > 0 ? (
                    <button type="button" onClick={applyPreset} className={ADMIN_ACTION_BUTTON}>
                      {PRESET_LABEL}
                    </button>
                  ) : null}
                </AdminBulkActionRow>
                <div className="mt-4">
                  <PythonTable
                    packages={draft.python}
                    rowVerify={rowVerify}
                    onRemove={removePython}
                    onVerifyRow={handleVerifyRow}
                    onPreset={applyPreset}
                  />
                </div>
              </AdminSectionCard>

              <AdvancedSection
                defaultOpen={drift}
                draft={draft}
                dockerfileSnippet={dockerfileSnippet}
                snapshotIdInput={snapshotIdInput}
                digestInput={digestInput}
                marking={marking}
                markError={markError}
                onUpdateApt={(index, value) => {
                  const next = [...draft.apt];
                  next[index] = value;
                  setDraft({ ...draft, apt: next });
                }}
                onRemoveApt={(index) => {
                  setDraft({ ...draft, apt: draft.apt.filter((_, i) => i !== index) });
                }}
                onAddApt={() => setDraft({ ...draft, apt: [...draft.apt, ''] })}
                onNotesChange={(value) => setDraft({ ...draft, notes: value })}
                onSnapshotIdChange={setSnapshotIdInput}
                onDigestChange={setDigestInput}
                onMarkDeployed={onMarkDeployed}
              />

              {saveError ? <InlineError>{saveError}</InlineError> : null}
              <AdminSaveBar
                dirty={dirty}
                saving={saving}
                onSave={onSave}
                onDiscard={onDiscard}
                saveLabel="Save manifest"
              />
            </>
          )}
        </div>
      </Commentable>
    </AdminSceneShell>
  );
}
